import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { ButtonGroup } from "@volca/ui/components/ui/button-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { useRecheckDocument } from "@/accounts-payable/query";
import type { ApAgentRun } from "@/accounts-payable/types";
import {
  allChangesBlocked,
  isPendingReviewAction,
  staleNoticePrimaryAction,
} from "@/accounts-payable/utils";
import { getErrorMessage } from "@/core/api/errors";
import { RerunAgentDialog } from "./rerun-agent-dialog";

const ACTIONS = {
  check: {
    label: "Check ServiceTitan",
    hint: "Re-read the values these proposals rest on.",
  },
  rerun: {
    label: "Re-run agent",
    hint: "Replace these proposals with new ones from ServiceTitan as it is now.",
  },
} as const;

/**
 * The stale warning's split button. The main half is the step that fits
 * (see `staleNoticePrimaryAction`): checking ServiceTitan until a recent check
 * found changes, then re-running the agent. The menu holds the other one. A
 * check that leaves nothing to approve (ServiceTitan would refuse every
 * changed proposal) goes straight on to the re-run confirm.
 */
export const StaleNoticeActions = ({
  run,
  businessId,
  staleAfterHours,
}: {
  run: ApAgentRun;
  businessId: string | null;
  staleAfterHours: number | undefined;
}) => {
  const recheck = useRecheckDocument(businessId, run.document_type);
  const [confirming, setConfirming] = useState<{ allRefused: boolean } | null>(
    null,
  );
  const primary = staleNoticePrimaryAction(run, staleAfterHours);
  const secondary = primary === "check" ? "rerun" : "check";

  const check = () =>
    recheck.mutate(run.document_id, {
      onSuccess: (checked) => {
        if (allChangesBlocked(checked)) {
          setConfirming({ allRefused: true });
          return;
        }
        const changed = checked.actions.filter(
          (a) => isPendingReviewAction(a) && (a.st_changes ?? []).length > 0,
        ).length;
        if (changed === 0) {
          toast.success("Nothing these proposals rest on has changed");
        } else {
          toast.info(
            changed === 1
              ? "1 proposal rests on values that changed"
              : `${changed} proposals rest on values that changed`,
          );
        }
      },
      onError: (error) =>
        toast.error("Couldn't check ServiceTitan", {
          description: getErrorMessage(error),
        }),
    });
  const act = (action: "check" | "rerun") =>
    action === "check" ? check() : setConfirming({ allRefused: false });

  return (
    <>
      <ButtonGroup className="flex-none">
        <Button
          size="sm"
          variant="outline"
          disabled={recheck.isPending}
          loading={primary === "check" && recheck.isPending}
          onClick={() => act(primary)}
        >
          {ACTIONS[primary].label}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                size="icon-sm"
                variant="outline"
                aria-label="More ways to refresh these proposals"
                disabled={recheck.isPending}
              />
            }
          >
            <CaretDownIcon />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuItem
              className="items-start"
              onClick={() => act(secondary)}
            >
              <Stack align="start" gap={0}>
                <Text>{ACTIONS[secondary].label}</Text>
                <Text variant="caption" tone="muted">
                  {ACTIONS[secondary].hint}
                </Text>
              </Stack>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </ButtonGroup>
      <RerunAgentDialog
        run={run}
        businessId={businessId}
        open={confirming !== null}
        onOpenChange={(open) => !open && setConfirming(null)}
        allRefused={confirming?.allRefused}
      />
    </>
  );
};
